import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PiggyBank, Plus, Trash2 } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Table, THead, td, Field, Button, Alert, Modal, ModalHeader, Input, Select } from '../components/ui'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }

const emptyForm = { teknisi_id: '', tanggal: new Date().toISOString().slice(0, 10), jenis: 'SETOR', jumlah: '', keterangan: '' }

export default function Tabungan() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [saldo, setSaldo] = useState([])
  const [pegawaiList, setPegawaiList] = useState([])
  const [loading, setLoading] = useState(true)
  // Preset dari tombol "Tabungan" di halaman Pegawai (?teknisi_id=X) -- pola sama dgn Kasbon.jsx
  const [filterTeknisi, setFilterTeknisi] = useState(searchParams.get('teknisi_id') || '')

  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [filterTeknisi])
  useEffect(() => { api.get('/pegawai').then(r => setPegawaiList(r.data.filter(p => p.aktif))) }, [])

  function ubahFilter(id) {
    setFilterTeknisi(id)
    setSearchParams(id ? { teknisi_id: id } : {})
  }

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/tabungan', { params: filterTeknisi ? { teknisi_id: filterTeknisi } : {} })
      setData(res.data.data)
      setSaldo(res.data.saldo)
    } finally { setLoading(false) }
  }

  function bukaTambah() {
    setForm({ ...emptyForm, teknisi_id: filterTeknisi || '' })
    setError('')
    setModal(true)
  }

  async function simpan() {
    if (!form.teknisi_id) return setError('Pegawai wajib dipilih')
    if (!form.tanggal) return setError('Tanggal wajib diisi')
    if (!form.jumlah || Number(form.jumlah) <= 0) return setError('Jumlah harus lebih dari 0')
    setSaving(true)
    try {
      await api.post('/tabungan', form)
      setModal(false)
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  async function hapus(row) {
    if (!window.confirm(`Hapus ${row.jenis === 'SETOR' ? 'setoran' : 'penarikan'} ${rp(row.jumlah)} milik ${row.nama_teknisi}?`)) return
    try {
      await api.delete(`/tabungan/${row.id}`)
      load()
    } catch (e) {
      alert(e.response?.data?.message || 'Gagal menghapus')
    }
  }

  const ringkasan = filterTeknisi ? saldo.find(s => String(s.teknisi_id) === String(filterTeknisi)) : null

  return (
    <div>
      <PageHeader
        title="Tabungan"
        subtitle="Simpanan pegawai -- otomatis bertambah dari Potongan Lain saat slip Payroll dibuat, atau setor/tarik manual"
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Entri</Button>}
      />

      {ringkasan ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Card style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{ringkasan.nama} — Saldo Tabungan</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--success)' }}>{rp(ringkasan.saldo)}</div>
          </Card>
        </div>
      ) : saldo.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          {saldo.map(s => (
            <Card key={s.teknisi_id} style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{s.nama}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--success)' }}>{rp(s.saldo)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>saldo tabungan</div>
            </Card>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 14, maxWidth: 260 }}>
        <Select value={filterTeknisi} onChange={e => ubahFilter(e.target.value)}>
          <option value="">Semua pegawai</option>
          {pegawaiList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
        </Select>
      </div>

      <Table>
        <THead cols={['Tanggal', 'Pegawai', 'Jenis', 'Jumlah', 'Keterangan', '']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={6}><EmptyState icon={PiggyBank} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={6}><EmptyState icon={PiggyBank} title="Belum ada riwayat tabungan" /></td></tr>
          ) : data.map(row => (
            <tr key={`${row.type}-${row.id}`}>
              <td style={td}>{row.tanggal}</td>
              <td style={{ ...td, fontWeight: 600 }}>{row.nama_teknisi}</td>
              <td style={td}>
                {row.type === 'otomatis'
                  ? <Badge tone="brand">Otomatis — Potongan Payroll {row.periode}</Badge>
                  : row.jenis === 'SETOR' ? <Badge tone="success">Setor Manual</Badge> : <Badge tone="warning">Tarik Manual</Badge>}
              </td>
              <td style={td}>
                {(row.type === 'otomatis' || row.jenis === 'SETOR')
                  ? <span style={{ color: 'var(--success)' }}>+{rp(row.jumlah)}</span>
                  : <span style={{ color: 'var(--danger)' }}>-{rp(row.jumlah)}</span>}
              </td>
              <td style={{ ...td, color: 'var(--text-muted)' }}>{row.keterangan || '-'}</td>
              <td style={td}>
                {row.type === 'manual' && (
                  <button onClick={() => hapus(row)} title="Hapus" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
                    <Trash2 size={15} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {modal && (
        <Modal onClose={() => setModal(false)} width={420}>
          <ModalHeader title="Tambah Entri Tabungan" onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Pegawai *" style={{ marginBottom: 14 }}>
            <Select value={form.teknisi_id} onChange={e => setForm({ ...form, teknisi_id: e.target.value })}>
              <option value="">Pilih pegawai</option>
              {pegawaiList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
            </Select>
          </Field>
          <Field label="Jenis *" style={{ marginBottom: 14 }}>
            <Select value={form.jenis} onChange={e => setForm({ ...form, jenis: e.target.value })}>
              <option value="SETOR">Setor (tambah saldo)</option>
              <option value="TARIK">Tarik (cairkan saldo)</option>
            </Select>
          </Field>
          <Field label="Tanggal *" style={{ marginBottom: 14 }}>
            <Input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} />
          </Field>
          <Field label="Jumlah (Rp) *" style={{ marginBottom: 14 }}>
            <Input type="number" value={form.jumlah} onChange={e => setForm({ ...form, jumlah: e.target.value })} placeholder="0" />
          </Field>
          <Field label="Keterangan" style={{ marginBottom: 8 }}>
            <Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} placeholder="mis. pencairan saat resign" />
          </Field>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
